import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    PropsWithChildren,
} from 'react';
import { Flashbar, FlashbarProps } from '@cloudscape-design/components';

type FlashItem = Omit<FlashbarProps.MessageDefinition, 'id' | 'dismissible' | 'onDismiss'> & {
    /** Optional auto-dismiss in ms. Successes auto-dismiss by default; errors do not. */
    autoDismissAfterMs?: number | null;
};

interface FlashbarContextValue {
    push: (item: FlashItem) => string;
    dismiss: (id: string) => void;
    clear: () => void;
}

const FlashbarContext = createContext<FlashbarContextValue | undefined>(undefined);

const DEFAULT_AUTO_DISMISS_MS = 6000;

/**
 * Global Flashbar provider. Mount once at the app root; child components push
 * transient notifications via useFlashbar(). Cloudscape Flashbar handles
 * aria-live announcements for free.
 */
export const FlashbarProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [items, setItems] = useState<FlashbarProps.MessageDefinition[]>([]);

    // Track auto-dismiss timers by message id so we can cancel them if the
    // user (or programmatic caller) dismisses the message early, clears all,
    // or unmounts the provider. Without this we'd leak timers and risk
    // calling setItems on an unmounted provider in tests.
    const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const clearTimerFor = useCallback((id: string) => {
        const handle = timersRef.current.get(id);
        if (handle !== undefined) {
            clearTimeout(handle);
            timersRef.current.delete(id);
        }
    }, []);

    const dismiss = useCallback((id: string) => {
        clearTimerFor(id);
        setItems((prev) => prev.filter((item) => item.id !== id));
    }, [clearTimerFor]);

    const push = useCallback((item: FlashItem): string => {
        const id = `flash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const { autoDismissAfterMs, ...rest } = item;
        const message: FlashbarProps.MessageDefinition = {
            ...rest,
            id,
            dismissible: true,
            onDismiss: () => dismiss(id),
        };
        setItems((prev) => [...prev, message]);

        // Successful + info messages auto-dismiss; warnings / errors persist
        // until the user acks them so they cannot miss something money-flow.
        const auto = autoDismissAfterMs === undefined
            ? (item.type === 'success' || item.type === 'info' ? DEFAULT_AUTO_DISMISS_MS : null)
            : autoDismissAfterMs;
        if (auto && auto > 0) {
            const handle = setTimeout(() => {
                timersRef.current.delete(id);
                setItems((prev) => prev.filter((m) => m.id !== id));
            }, auto);
            timersRef.current.set(id, handle);
        }
        return id;
    }, [dismiss]);

    const clear = useCallback(() => {
        timersRef.current.forEach((handle) => clearTimeout(handle));
        timersRef.current.clear();
        setItems([]);
    }, []);

    // Cancel any outstanding auto-dismiss timers when the provider unmounts.
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach((handle) => clearTimeout(handle));
            timers.clear();
        };
    }, []);

    const value = useMemo<FlashbarContextValue>(() => ({ push, dismiss, clear }), [push, dismiss, clear]);

    return (
        <FlashbarContext.Provider value={value}>
            {/* Render the flashbar only when there's something to show, to avoid
                an empty wrapper occupying space at the top of the layout. */}
            {items.length > 0 && (
                <div style={{ marginBottom: 'var(--space-scaled-m, 12px)' }}>
                    <Flashbar items={items} />
                </div>
            )}
            {children}
        </FlashbarContext.Provider>
    );
};

export function useFlashbar(): FlashbarContextValue {
    const ctx = useContext(FlashbarContext);
    if (!ctx) {
        throw new Error('useFlashbar must be used within a FlashbarProvider');
    }
    return ctx;
}
